import { useCallback, useId, useRef, useState, type ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Link,
  List,
  ListItem,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { isApiError, GUARDIAN_LINK_ERROR_CODES } from "@bw/shared/api";
import { formatDateUtc } from "@bw/shared/utils";
import type {
  GuardianActivityRequestView,
  GuardianLinkResponse,
} from "@bw/shared/types/generated";
import { Page } from "../../../common/components/Page";
import { GUARDIAN_CONTROL_COPY } from "../../../common/guardian/controls";
import { CoachingInvitations } from "./CoachingInvitations";
import {
  useChildApprovals,
  useActiveChildren,
  useApproveChild,
  useDecideFamilyRequest,
  useFamilyRequests,
  useRevokeChild,
} from "../../../common/query/useGuardianLink";
import { visuallyHidden } from "../../../common/visuallyHidden";

/**
 * FamilyApprovalsPage (/family/approvals) — the PARENT side of COPPA
 * parent-managed under-13 consent (#1309 Part A).
 *
 * A real, authenticated BW account holder lands here from the request email
 * ("<child> wants you to approve their account"). The page has TWO sections, both
 * server-scoped to their VERIFIED email (the raw child uid is nulled — each row is
 * identified by `child_display_name`, no-UID-in-UI):
 *
 *   1. PENDING approvals — the child-link requests awaiting action. Each lets the
 *      parent APPROVE (records the guardian waiver server-side + activates the
 *      child).
 *   2. APPROVED children — the parent's roster of active guardian links (the COPPA
 *      right-to-withdraw surface, #1630). Each lets the parent PAUSE access
 *      (multi-guardian #2691: the account_active revoke; the parent stays a
 *      guardian), through a brief inline confirm, never a bare button. Leaving
 *      is a separate action on the child's Guardians & access page.
 *
 * There is NO Stripe / charge here — the parent acts from inside their own
 * authenticated account (public-profile unlock is a separate slice, #1624).
 *
 * Approve is GATED on an explicit guardian-waiver affirmation (a required
 * checkbox + legal links, the ConsentStep pattern) — a real affirmation, never
 * implicit. The waiver itself is recorded by the backend on approve; the checkbox
 * is the consent gate that enables the button.
 *
 * The AppShell supplies no page title, so this routed page OWNS its single <h1>.
 * It renders that heading itself (rather than via the <Page title> prop) so it can
 * hold a ref and serve as the post-approve focus target (#1625 3c) — see
 * handleApproved.
 */
export function FamilyApprovalsPage() {
  const approvals = useChildApprovals();
  const active = useActiveChildren();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const activeHeadingRef = useRef<HTMLHeadingElement>(null);

  // #1625 (3c): approving a child removes its card (the pending row leaves the
  // re-read list). The Approve button it had focus on unmounts → focus would fall
  // to <body>, stranding keyboard/SR users. Pull focus back to the page heading so
  // they are re-oriented to the (now shorter, or empty) list. The page owns its
  // single <h1> (the AppShell supplies no page title) and makes it focusable
  // (tabIndex={-1}) as the focus target.
  const handleApproved = useCallback(() => {
    headingRef.current?.focus();
  }, []);

  // Same a11y restoration on revoke: the revoked row unmounts (it leaves the
  // active roster), so move focus to the "Approved children" section heading
  // rather than letting it drop to <body>.
  const handleRevoked = useCallback(() => {
    activeHeadingRef.current?.focus();
  }, []);

  return (
    <Page>
      <Typography component="h1" sx={{ fontSize: "1.375rem", fontWeight: 700 }} tabIndex={-1} ref={headingRef}>
        Family approvals
      </Typography>
      <Stack spacing={4} sx={{ mt: 2 }}>
        <CoachingInvitations />
        <Typography color="text.secondary">
          Approve a child who asked you to be their parent or guardian on
          Badminton Warriors, and manage the children you have already approved.
        </Typography>

        <ActivityRequests />

        <Stack component="section" spacing={1.5} aria-labelledby="pending-h">
          <Typography component="h2" variant="h6" id="pending-h">
            Pending approvals
          </Typography>
          {approvals.isLoading ? (
            <LoadingLine>Loading approval requests…</LoadingLine>
          ) : approvals.isError || !approvals.data ? (
            <Alert severity="error">
              We couldn&apos;t load your approval requests. Please try again.
            </Alert>
          ) : approvals.data.length === 0 ? (
            <Typography color="text.secondary" role="status">
              No pending approvals.
            </Typography>
          ) : (
            <List disablePadding aria-label="Pending approvals">
              {approvals.data.map((link) => (
                <ListItem key={link.link_id} disableGutters sx={{ mb: 1.5 }}>
                  <ChildApprovalCard link={link} onApproved={handleApproved} />
                </ListItem>
              ))}
            </List>
          )}
        </Stack>

        <Stack component="section" spacing={1.5} aria-labelledby="active-h">
          <Typography
            component="h2"
            variant="h6"
            id="active-h"
            tabIndex={-1}
            ref={activeHeadingRef}
          >
            Approved children
          </Typography>
          {active.isLoading ? (
            <LoadingLine>Loading your approved children…</LoadingLine>
          ) : active.isError || !active.data ? (
            <Alert severity="error">
              We couldn&apos;t load your approved children. Please try again.
            </Alert>
          ) : active.data.length === 0 ? (
            <Typography color="text.secondary" role="status">
              No approved children yet.
            </Typography>
          ) : (
            <List disablePadding aria-label="Approved children">
              {active.data.map((link) => (
                <ListItem key={link.link_id} disableGutters sx={{ mb: 1.5 }}>
                  <ActiveChildRow link={link} onRevoked={handleRevoked} />
                </ListItem>
              ))}
            </List>
          )}
        </Stack>
      </Stack>
    </Page>
  );
}

/**
 * Requests (#2691): what each child asked to do while a control is set to Ask.
 * Any guardian decides; the first decision counts, and a late one is told who
 * decided. Decided requests stay listed with who decided them.
 */
function ActivityRequests() {
  const requests = useFamilyRequests("all");
  const rows = requests.data ?? [];
  const pending = rows.filter((row) => row.status === "pending");
  const decided = rows.filter((row) => row.status !== "pending").slice(0, 10);
  // A decided row leaves the waiting list, taking the focused button with it:
  // put focus on the section heading so keyboard and screen-reader users keep
  // their place.
  const headingRef = useRef<HTMLHeadingElement>(null);
  const focusHeading = useCallback(() => headingRef.current?.focus(), []);
  // Requests only arise once a guardian sets a control to Ask, so an empty
  // list is the common case: show nothing rather than an empty section.
  if (requests.isSuccess && rows.length === 0) return null;
  return (
    <Stack component="section" spacing={1.5} aria-labelledby="requests-h">
      <Typography component="h2" variant="h6" id="requests-h" ref={headingRef} tabIndex={-1} sx={{ outline: "none" }}>
        Requests
      </Typography>
      {requests.isLoading ? (
        <LoadingLine>Loading requests…</LoadingLine>
      ) : requests.isError ? (
        <Alert severity="error">We couldn&apos;t load your children&apos;s requests. Please try again.</Alert>
      ) : (
        <>
          {pending.length === 0 ? (
            <Typography color="text.secondary" role="status">
              Nothing waiting for a decision.
            </Typography>
          ) : (
            <List disablePadding aria-label="Requests waiting for a decision">
              {pending.map((row) => (
                <ListItem key={row.request_id} disableGutters sx={{ mb: 1.5 }}>
                  <PendingRequestRow row={row} onDecided={focusHeading} />
                </ListItem>
              ))}
            </List>
          )}
          {decided.length > 0 ? (
            <List disablePadding aria-label="Recently decided requests">
              {decided.map((row) => (
                <ListItem key={row.request_id} disableGutters sx={{ mb: 1 }}>
                  <DecidedRequestRow row={row} />
                </ListItem>
              ))}
            </List>
          ) : null}
        </>
      )}
    </Stack>
  );
}

function requestSummary(row: GuardianActivityRequestView): string {
  return `${GUARDIAN_CONTROL_COPY[row.control].label}: ${row.subject_label}`;
}

function PendingRequestRow({ row, onDecided }: { row: GuardianActivityRequestView; onDecided: () => void }) {
  const decide = useDecideFamilyRequest();
  const childName = row.child_display_name?.trim() || "Your child";
  const context = `${childName}, ${requestSummary(row)}`;
  const run = (decision: "approve" | "decline") =>
    decide.mutate({ requestId: row.request_id, decision }, { onSuccess: onDecided });
  return (
    <Paper variant="outlined" sx={{ width: "100%", p: 2.5 }}>
      <Stack spacing={1.5}>
        <Stack spacing={0.25}>
          <Typography>
            <strong>{childName}</strong> is asking to go ahead
          </Typography>
          <Typography sx={{ fontWeight: 700 }}>{requestSummary(row)}</Typography>
          <Typography variant="body2" color="text.secondary">
            Asked {formatDateUtc(row.created_at)}. Approving lets them go ahead with this person or
            place from now on; you can remove it later from their page.
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            disabled={decide.isPending}
            aria-label={`Approve: ${context}`}
            onClick={() => run("approve")}
          >
            {decide.isPending && decide.variables?.decision === "approve" ? "Approving…" : "Approve"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={decide.isPending}
            aria-label={`Decline: ${context}`}
            onClick={() => run("decline")}
          >
            {decide.isPending && decide.variables?.decision === "decline" ? "Declining…" : "Decline"}
          </Button>
        </Stack>
        {decide.isError ? (
          <Alert severity={isApiError(decide.error) && decide.error.status === 409 ? "info" : "error"}>
            {isApiError(decide.error) && decide.error.status === 409
              ? decide.error.message
              : mutationErrorMessage(decide.error)}
          </Alert>
        ) : null}
      </Stack>
    </Paper>
  );
}

/** Why an expired request closed, as the server recorded it. */
const EXPIRED_REASON_LABEL: Record<NonNullable<GuardianActivityRequestView["expired_reason"]> | "unknown", string> = {
  timed_out: "No guardian decided in time",
  setting_changed: "Closed when a guardian changed this setting",
  unavailable: "That person or place is no longer available",
  unknown: "No longer waiting for a decision",
};

function DecidedRequestRow({ row }: { row: GuardianActivityRequestView }) {
  const childName = row.child_display_name?.trim() || "Your child";
  const who = row.decided_by_you ? "you" : row.decided_by_display_name ?? "a guardian";
  const outcome: Record<GuardianActivityRequestView["status"], { label: string; color: "success" | "default" | "warning" }> = {
    pending: { label: "Waiting", color: "default" },
    approved: { label: "Approved", color: "success" },
    declined: { label: "Declined", color: "default" },
    revoked: { label: "Removed", color: "default" },
    expired: { label: "Expired", color: "default" },
    withdrawn: { label: "Withdrawn", color: "default" },
  };
  const status = outcome[row.status];
  const verb = { approved: "Approved", declined: "Declined", revoked: "Removed" } as const;
  const decidedBy =
    row.status in verb && row.decided_at
      ? `${verb[row.status as keyof typeof verb]} by ${who} on ${formatDateUtc(row.decided_at)}`
      : row.status === "expired"
        ? EXPIRED_REASON_LABEL[row.expired_reason ?? "unknown"]
        : row.status === "withdrawn"
          ? `${childName} withdrew it`
          : null;
  return (
    <Paper variant="outlined" sx={{ width: "100%", p: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{ alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between" }}
      >
        <Stack sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>
            {childName} · {requestSummary(row)}
          </Typography>
          {decidedBy ? (
            <Typography variant="body2" color="text.secondary">
              {decidedBy}
            </Typography>
          ) : null}
        </Stack>
        <Chip size="small" variant="outlined" color={status.color} label={status.label} />
      </Stack>
    </Paper>
  );
}

function LoadingLine({ children }: { children: ReactNode }) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      role="status"
      sx={{ alignItems: "center" }}
    >
      <CircularProgress size={20} />
      <Typography color="text.secondary">{children}</Typography>
    </Stack>
  );
}

/**
 * Map an approve/revoke ApiError to an inline, actor-actionable message. The
 * backend owns the authorization — the page only renders the right copy by
 * `code` (never a raw uid / opaque message).
 */
function mutationErrorMessage(error: unknown): ReactNode {
  const code = isApiError(error) ? error.code : null;
  // Match on the EXACT backend literals (GUARDIAN_LINK_ERROR_CODES, the FE/BE
  // single source of truth) — inline strings are how these drifted from the
  // backend originally (the parent page matched `LINK_NOT_FOUND`, which the
  // backend never emits, so an already-consumed link fell to the generic copy).
  switch (code) {
    case GUARDIAN_LINK_ERROR_CODES.EMAIL_NOT_VERIFIED:
      return (
        <>
          Please verify your email first.{" "}
          <Link
            component={RouterLink}
            to="/verify-email?returnUrl=%2Fme%2Ffamily%2Fapprovals"
          >
            Verify your email
          </Link>
          , then try again.
        </>
      );
    case GUARDIAN_LINK_ERROR_CODES.NOT_THE_PARENT:
      return "You're not the parent named on this request.";
    case GUARDIAN_LINK_ERROR_CODES.CHILD_NOT_PENDING:
      return "This account no longer needs approval.";
    case GUARDIAN_LINK_ERROR_CODES.NOT_FOUND:
      return "This request is no longer available.";
    case GUARDIAN_LINK_ERROR_CODES.MUST_BE_ADULT:
      return "Guardians must be adults, and a child can't approve their own account.";
    case GUARDIAN_LINK_ERROR_CODES.DOB_REQUIRED:
      return (
        <>
          Confirm your date of birth on{" "}
          <Link component={RouterLink} to="/me/edit">
            your profile
          </Link>{" "}
          first, then try again.
        </>
      );
    case GUARDIAN_LINK_ERROR_CODES.LIMIT_REACHED:
      return "This child already has the most guardians allowed.";
    case GUARDIAN_LINK_ERROR_CODES.RATE_LIMITED:
      return "Too many requests for this child today. Try again tomorrow.";
    case GUARDIAN_LINK_ERROR_CODES.ALREADY_EXISTS:
      return "You're already a guardian for this child.";
    default:
      return "We couldn't complete that right now. Please try again.";
  }
}

/**
 * One pending child-link request. Owns its own guardian-waiver affirmation gate
 * (a required checkbox) + the approve mutation (per-card pending/error). Revoke
 * is NOT here — an approved child is revoked from the "Approved children" section
 * below (the link is active there, never pending here).
 */
function ChildApprovalCard({
  link,
  onApproved,
}: {
  link: GuardianLinkResponse;
  onApproved: () => void;
}) {
  const approve = useApproveChild();
  const [agreed, setAgreed] = useState(false);
  const checkboxId = useId();

  // Never a raw uid — the backend nulls child_user_id on parent-facing rows; the
  // parent identifies the child by display name (no-UID-in-UI).
  const childName = link.child_display_name?.trim() || "This child";

  return (
    <Paper variant="outlined" sx={{ width: "100%", p: 2.5 }}>
      <Stack spacing={2}>
        <Typography>
          {link.is_co_guardian_invite ? (
            <>
              You&apos;ve been invited to help manage{" "}
              <strong>{childName}</strong>&apos;s Badminton Warriors account as one
              of their guardians.
            </>
          ) : (
            <>
              <strong>{childName}</strong> wants you to approve their Badminton
              Warriors account.
            </>
          )}
        </Typography>

        {/* Guardian-waiver affirmation (ConsentStep pattern): a reading prompt with
          the legal links ABOVE, then a required checkbox gating Approve. The links
          are deliberately OUTSIDE the <label> — a label must not wrap interactive
          controls (#1625 3b, nested-interactive). The waiver is RECORDED
          server-side on approve; this checkbox is the explicit consent gate. */}
        <Typography variant="body2" color="text.secondary">
          Please read the Badminton Warriors{" "}
          <Link component={RouterLink} to="/terms">
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link component={RouterLink} to="/privacy">
            Privacy Policy
          </Link>
          .
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              id={checkboxId}
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              slotProps={{ input: { "aria-required": true } }}
            />
          }
          label={
            <Typography variant="body2">
              I am {childName}&apos;s parent or guardian and I consent to their
              use of Badminton Warriors under the Terms of Service and Privacy
              Policy.
            </Typography>
          }
          sx={{ alignItems: "flex-start", m: 0 }}
        />

        <Button
          variant="contained"
          size="small"
          fullWidth
          disabled={!agreed || approve.isPending}
          onClick={() =>
            approve.mutate(link.link_id, { onSuccess: onApproved })
          }
        >
          {approve.isPending
            ? "Approving…"
            : link.is_co_guardian_invite
              ? "Become a guardian"
              : "Approve account"}
        </Button>

        {approve.isError ? (
          <Alert severity="error">{mutationErrorMessage(approve.error)}</Alert>
        ) : null}
      </Stack>
    </Paper>
  );
}

/**
 * One APPROVED child (an active guardian link) — the COPPA right-to-withdraw row
 * (#1630). Shows the child's display name + when consent was granted, and a
 * Pause control (the child loses access until a guardian turns it back on; the
 * row stays, marked Paused), behind a brief inline confirm — never a bare,
 * one-click button. Owns its own pause mutation (per-row pending/error). The
 * child is always identified by `child_display_name`, never a raw UID
 * (no-UID-in-UI; the backend nulls `child_user_id` on parent-facing rows).
 */
function ActiveChildRow({
  link,
  onRevoked,
}: {
  link: GuardianLinkResponse;
  onRevoked: () => void;
}) {
  const revoke = useRevokeChild();
  const [confirming, setConfirming] = useState(false);

  const childName = link.child_display_name?.trim() || "This child";

  return (
    <Paper variant="outlined" sx={{ width: "100%", p: 2.5 }}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", justifyContent: "space-between" }}
        >
          <Stack>
            <Typography sx={{ fontWeight: 700 }}>{childName}</Typography>
            <Typography variant="body2" color="text.secondary">
              Approved {formatDateUtc(link.consented_at)}
            </Typography>
          </Stack>
          <Button
            component={RouterLink}
            to={`/me/family/children/${encodeURIComponent(link.link_id)}`}
            variant="outlined"
            size="small"
          >
            Guardians &amp; access
          </Button>
        </Stack>

        {link.child_paused ? (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Chip size="small" color="warning" label="Paused" />
            <Typography variant="body2" color="text.secondary">
              Turn access back on from Guardians &amp; access.
            </Typography>
          </Stack>
        ) : confirming ? (
          <Stack
            spacing={1.5}
            role="group"
            aria-label={`Pause access for ${childName}`}
          >
            <Typography variant="body2">
              Pause <strong>{childName}</strong>&apos;s account? They can&apos;t
              use Badminton Warriors until you or another guardian turns access
              back on. You stay their guardian and booked sessions stay
              scheduled. To stop being a guardian, use Leave on the Guardians
              &amp; access page.
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                color="error"
                variant="contained"
                size="small"
                disabled={revoke.isPending}
                onClick={() =>
                  revoke.mutate(link.link_id, {
                    onSuccess: () => {
                      setConfirming(false);
                      onRevoked();
                    },
                  })
                }
              >
                {revoke.isPending ? "Pausing…" : "Yes, pause"}
              </Button>
              <Button
                variant="text"
                size="small"
                disabled={revoke.isPending}
                onClick={() => setConfirming(false)}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack direction="row">
            <Button
              color="error"
              variant="text"
              size="small"
              onClick={() => setConfirming(true)}
            >
              Pause access
            </Button>
          </Stack>
        )}

        {revoke.isError ? (
          <Alert severity="error">{mutationErrorMessage(revoke.error)}</Alert>
        ) : null}
      </Stack>
    </Paper>
  );
}
